import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import {
  getPreferredLanguage, setPreferredLanguage, languageOfPath, pathIn,
} from '../utils/languagePreference';
import { documentNavigationType } from '../utils/navigation';

// Menu settings outrank history. Browser Back / Forward (in the app, or back
// into the site from another site) may land on a page that was viewed in a
// different language; it is shown in the language chosen in the menu instead,
// by replacing that history entry, so Go back only browses pages and never
// undoes the language setting. `restoreKey` tells ScrollToTop which entry's
// scroll position to restore on the replaced page, and `restoreLang` (the
// language the page was shown in before) which language button to focus.
//
// Any other way of reaching a page — the language menu, a link (e.g. the
// sitemap's language section), a typed or bookmarked URL, a reload — shows it
// in its own language, which becomes the setting.
//
// A page restored from the browser's back/forward cache (Back to a page of the
// site that was left for another document) is shown exactly as it was left,
// without a render: it is caught on `pageshow` instead.
export default function LanguageOverHistory() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const firstRender = useRef(true);
  const current = useRef(location);
  current.current = location;

  // Replace the current entry with the same page in the chosen language.
  // `from` (the list a collection was opened from, used by "Back to …") is a
  // path in the old language too.
  const showIn = useRef(null);
  showIn.current = (preferred, loc) => {
    const from = loc.state?.from;
    navigate(pathIn(preferred, loc), {
      replace: true,
      state: {
        ...loc.state,
        ...(from ? { from: pathIn(preferred, { pathname: from, search: '' }) } : {}),
        restoreKey: loc.key,
        restoreLang: languageOfPath(loc.pathname),
      },
    });
  };

  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return;
      const loc = current.current;
      const urlLang = languageOfPath(loc.pathname);
      const preferred = getPreferredLanguage();
      if (urlLang && preferred && preferred !== urlLang) showIn.current(preferred, loc);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Layout effect: the redirect happens before the browser paints, so the
  // page never flashes in the old language.
  useLayoutEffect(() => {
    const initial = firstRender.current;
    firstRender.current = false;
    const urlLang = languageOfPath(location.pathname);
    if (!urlLang) return;

    const fromHistory = initial ? documentNavigationType() === 'back_forward' : navType === 'POP';
    const preferred = getPreferredLanguage();
    if (!fromHistory || !preferred) {
      setPreferredLanguage(urlLang);
      return;
    }
    if (preferred !== urlLang) showIn.current(preferred, location);
  }, [location, navType]);

  return null;
}
